import { HttpEvent, HttpEventType } from '@angular/common/http';
import { Component, inject, Inject, OnInit, signal } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { faClose, faFile, faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';

import { catchError, forkJoin, Observable, of, tap } from 'rxjs';

import { NotificationLevel, NotificationsService } from '@eiq/notifications';

import { FileType, FileTypeExtension, FileTypeTexts, ImageTypes } from '@empathy/global/data';

import { FilePreview } from '../../models/file-preview.interface';
import { Media } from '../../models/media.model';
import { StimulusPickerSettings } from '../../models/stimulus-picker-settings.interface';
import { MediaService } from '../../services/media.service';

@Component({
  selector: 'eiq-media-picker-dialog',
  templateUrl: 'media-picker-dialog.component.html',
  styleUrls: ['./media-picker-dialog.component.scss']
})
export class MediaPickerDialogComponent implements OnInit {
  fileTypeTexts$ = signal<FileTypeExtension[]>([]);
  uploadedFilesPreview$ = signal<FilePreview[]>([]);
  uploadedFiles$ = signal<Media[]>([]);
  uploading$ = signal<boolean>(false);

  faClose = faClose;
  faFile = faFile;
  faPlus = faPlus;
  faTrash = faTrash;
  fileType = FileType;
  maxSize = 50; // MB

  private uploadedFiles: File[] = [];

  private mediaService = inject(MediaService);
  private not = inject(NotificationsService);

  constructor(
    private dialogRef: MatDialogRef<MediaPickerDialogComponent, Media[]>,
    @Inject(MAT_DIALOG_DATA) public data: StimulusPickerSettings
  ) {}

  ngOnInit(): void {
    this.dialogRef.updateSize('560px');
    this.dialogRef.disableClose = true;

    this.fileTypeTexts$.set(this.data.types.map((type) => FileTypeTexts[type]));
  }

  onSave(): void {
    const validFiles = this.uploadedFiles.filter((_v, index) => !this.uploadedFilesPreview$()[index].failed);

    if (validFiles.length === 0) {
      return;
    }

    if (!!this.data.maxNumberOfFiles && validFiles.length > this.data.maxNumberOfFiles) {
    }

    this.uploading$.set(true);
    const uploadCalls = validFiles.map((file) => {
      if (this.data.upload) {
        return this.uploadFile(this.data.upload, file);
      }

      const fileData = new FormData();
      fileData.append('mediaFile', file);
      fileData.append('itemReference', file.name);
      fileData.append('referenceId', this.data.referenceId ?? '');

      return this.mediaService.uploadStimulus(fileData).pipe(
        tap((mediaEvent: HttpEvent<{ id: string }>) => {
          if (!mediaEvent) {
            this.not.open({
              level: NotificationLevel.Error,
              title: `There has been an error uploading the file: ${file.name}`,
              message: ''
            });
            return;
          }

          if ((mediaEvent as any).body) {
            this.uploadedFiles$.update((files) => [...files, { mediaId: (mediaEvent as any).body.id, name: file.name, type: file.type } as Media]);
          }

          if (mediaEvent.type === HttpEventType.Response) {
            this.uploading$.set(false);
          }
        }),
        catchError((err) => {
          this.not.open({
            level: NotificationLevel.Error,
            title: `There has been an error uploading the file: ${file.name}`,
            message: err.message
          });
          return of(null);
        })
      );
    });

    if (uploadCalls.some((call) => call === null)) {
      this.uploading$.set(false);
      return;
    }

    forkJoin(uploadCalls).subscribe(() => {
      this.uploading$.set(false);
      this.dialogRef.close(this.uploadedFiles$());
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onUploadMore(event): void {
    const files = event.target.files;

    for (let i = 0; i < files.length; i++) {
      const fileToUpload = files.item(i);
      if (fileToUpload) {
        this.onUpload(fileToUpload);
      }
    }
  }

  onUpload(file: File): void {
    if (this.uploadedFiles.length === 0) {
      this.handleDialogSize(true);
    }

    this.uploadedFiles.push(file);

    const reader = new FileReader();
    reader.onload = (e): void => {
      this.uploadedFilesPreview$.update((files) => {
        if (!files) {
          files = [];
        }
        const filePreview: FilePreview = {
          file: file,
          size: (file.size / (1024 * 1024)).toFixed(2),
          src: e.target?.result as string | ArrayBuffer,
          failed: file.size > this.maxSize * 1024 * 1024,
          isImage: ImageTypes.includes(file.type as FileType)
        };
        files.push(filePreview);
        return files;
      });
    };
    reader.readAsDataURL(file);
  }

  onDelete(fileIndex: number): void {
    this.uploadedFiles.splice(fileIndex, 1);
    if (this.uploadedFiles.length === 0) {
      this.handleDialogSize(false);
    }

    this.uploadedFilesPreview$.update((files) => {
      if (files) {
        files.splice(fileIndex, 1);
      }
      return files;
    });
  }

  private uploadFile(upload: NonNullable<StimulusPickerSettings['upload']>, file: File): Observable<Media | null> {
    return upload(file).pipe(
      tap((media) => {
        if (media) {
          this.uploadedFiles$.update((files) => [...files, media]);
        }
      }),
      catchError((err) => {
        this.not.open({
          level: NotificationLevel.Error,
          title: `There has been an error uploading the file: ${file.name}`,
          message: err.message
        });
        return of(null);
      })
    );
  }

  private handleDialogSize(increase: boolean): void {
    if (increase) {
      if (this.data.multipleInd) {
        this.dialogRef.updateSize('1024px', '778px');
      } else {
        this.dialogRef.updateSize('560px', '587px');
      }
    } else {
      this.dialogRef.updateSize('560px');
    }
  }
}
